import { useState, type CSSProperties } from 'react'
import { useQuery } from '@tanstack/react-query'
import DashStatCard from '../../components/ui/DashStatCard'
import LineChart    from '../../components/ui/LineChart'
import DashIcon     from '../../components/ui/DashIcon'
import StatusBadge  from '../../components/ui/StatusBadge'
import { analyticsAPI }  from '../../api/analytics'
import { deliveriesAPI } from '../../api/deliveries'
import type { Delivery, PaginatedResponse } from '../../types'

const FALLBACK_WEEKS    = ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim']
const FALLBACK_GAINS    = [62, 84, 71, 96, 112, 134, 102]

const PERIOD_LABELS: Record<string, string> = {
  week: 'Semaine', month: 'Mois', year: 'Année',
}

function DonutChart({ data }: { data: { label: string; value: number; color: string }[] }) {
  const sum = data.reduce((a, b) => a + b.value, 0)
  const cx = 70, cy = 70, r = 52, sw = 18
  const C = 2 * Math.PI * r
  let cum = 0
  return (
    <svg className="donut" viewBox="0 0 140 140">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#E6ECF3" strokeWidth={sw} />
      {data.map((d, i) => {
        const frac = d.value / sum
        const dash = frac * C
        const offset = -cum * C
        cum += frac
        return (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none"
            stroke={d.color} strokeWidth={sw}
            strokeDasharray={`${dash.toFixed(2)} ${(C - dash).toFixed(2)}`}
            strokeDashoffset={offset.toFixed(2)}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        )
      })}
      <text x={cx} y={cy + 2} textAnchor="middle" fill="#0F2A4A"
        fontFamily="Space Grotesk" fontWeight="700" fontSize="22">
        {sum.toLocaleString()}
      </text>
      <text x={cx} y={cy + 22} textAnchor="middle" fill="#5B6B7E"
        fontSize="10" letterSpacing="1">TOTAL
      </text>
    </svg>
  )
}

export default function LivreurRevenus() {
  const [period, setPeriod] = useState<'week' | 'month' | 'year'>('month')

  const { data: stats } = useQuery<{
    period: string
    parcels: { total: number; delivered: number; failed: number; successRate: number }
    revenue: { total: number; average: number; currency: string }
    topExpediteur: { id: string; name: string; parcels: number } | null
  }>({
    queryKey: ['livreur-stats', period],
    queryFn: async () => {
      const res = await analyticsAPI.getLivreurStats(period)
      return res.data
    },
  })

  const { data: deliveriesData, isLoading: loadingDeliveries } = useQuery<PaginatedResponse<Delivery>>({
    queryKey: ['livreur-delivered', period],
    queryFn: async () => {
      const res = await deliveriesAPI.getAll({ status: 'delivered', limit: 10 })
      return res
    },
  })

  const revenue       = stats?.revenue?.total ?? 0
  const totalDel      = stats?.parcels?.total ?? 0
  const activeHours   = 0
  const cashToDeposit = 0
  const bonuses       = 0
  const rating        = 0
  const walletBalance = revenue

  const chartGains  = FALLBACK_GAINS
  const chartLabels = FALLBACK_WEEKS
  const chartTotal  = chartGains.reduce((a, b) => a + b, 0)
  const chartObjectif = chartGains.map(() => Math.round(revenue / Math.max(chartGains.length, 1)))

  const bonusDistance = 0
  const tips          = 0
  const bonusHours    = 0
  const baseRevenue   = Math.max(0, revenue - bonusDistance - tips - bonusHours)

  const rawSources = [
    { label: 'Livraisons',     value: baseRevenue,   color: '#7CB342' },
    { label: 'Bonus distance', value: bonusDistance, color: '#4FA0E8' },
    { label: 'Pourboires',     value: tips,          color: '#F3A845' },
    { label: 'Bonus heures',   value: bonusHours,    color: '#7C5CD3' },
  ].filter(s => s.value > 0)
  const SOURCES = rawSources.length > 0 ? rawSources : [{ label: 'Livraisons', value: revenue, color: '#7CB342' }]

  const deliveries = deliveriesData?.data ?? []

  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })

  return (
    <>
      <div className="page-head">
        <div className="ph-titles">
          <h1>Revenus</h1>
          <p>Suivi détaillé de tes gains, payouts et bonus.</p>
        </div>
        <div className="ph-actions">
          <div className="seg">
            {(['week', 'month', 'year'] as const).map(p => (
              <button key={p} className={period === p ? 'is-active' : ''} onClick={() => setPeriod(p)}>
                {PERIOD_LABELS[p]}
              </button>
            ))}
          </div>
          <button className="btn btn-ghost"><DashIcon name="download" size={14} /> Export PDF</button>
        </div>
      </div>

      <div className="detail-grid" style={{ '--detail-cols': '380px 1fr', gap: 18, marginBottom: 18 } as CSSProperties}>
        <div className="wallet-card">
          <div className="w-label">Solde disponible</div>
          <div className="w-amount">{walletBalance.toLocaleString('fr-TN')}<sup>DT</sup></div>
          <div className="w-meta">
            <span>Prochain virement: <b style={{ color: '#fff' }}>Vendredi prochain</b></span>
          </div>
          <div className="w-actions">
            <button className="w-btn w-btn-primary"><DashIcon name="download" size={14} />Retirer</button>
            <button className="w-btn"><DashIcon name="card" size={14} />Méthodes</button>
          </div>
        </div>

        <div className="stat-grid stat-grid-3">
          <DashStatCard
            label={`Ce ${PERIOD_LABELS[period].toLowerCase()}`}
            value={`${revenue.toLocaleString('fr-TN')} DT`}
            trend="+18% vs période précédente" icon="wallet"
            spark={[100,140,180,220,260,180,300,340,280,360,400,revenue > 460 ? revenue : 460]}
          />
          <DashStatCard label="Livraisons" value={`${totalDel}`} trend={`+${Math.floor(totalDel * 0.15)} ce ${PERIOD_LABELS[period].toLowerCase()}`} icon="package" />
          <DashStatCard label="Heures actives" value={`${activeHours}h`} trend={`${(revenue / Math.max(activeHours, 1)).toFixed(1)} DT/h moyen`} icon="clock" />
          <DashStatCard label="Cash à déposer" value={`${cashToDeposit} DT`} trend="Livraisons COD" icon="cash" accent="dark" />
          <DashStatCard label="Bonus actifs" value={`${bonuses}`} trend="Distance + Étoile" icon="zap" />
          <DashStatCard label="Note moyenne" value={`${rating} ★`} trend="+0.1 vs période" icon="star" />
        </div>
      </div>

      <div className="detail-grid" style={{ '--detail-cols': '1.4fr 1fr', gap: 18, marginBottom: 18 } as CSSProperties}>
        <div className="card chart-card">
          <div className="chart-head">
            <div>
              <div style={{ fontSize: '.78rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 600 }}>
                Gains par jour
              </div>
              <div style={{ fontFamily: 'Space Grotesk', fontSize: '1.4rem', fontWeight: 700, color: 'var(--navy-900)', marginTop: 2 }}>
                {chartTotal.toLocaleString('fr-TN')} DT {period === 'week' ? 'cette semaine' : period === 'month' ? 'ce mois' : 'cette année'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 16, fontSize: '.82rem' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--muted)' }}>
                <span style={{ width: 12, height: 3, background: 'var(--navy-900)', borderRadius: 2, display: 'inline-block' }} /> Gains
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--muted)' }}>
                <span style={{ width: 12, height: 3, background: 'var(--green-500)', borderRadius: 2, display: 'inline-block' }} /> Objectif
              </span>
            </div>
          </div>
          <div className="chart-body">
            <LineChart series={[{ data: chartGains }, { data: chartObjectif }]} labels={chartLabels} />
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h3>Source des revenus</h3>
            <span style={{ fontSize: '.78rem', color: 'var(--muted)' }}>{PERIOD_LABELS[period]}</span>
          </div>
          <div className="donut-wrap">
            <DonutChart data={SOURCES} />
            <div className="donut-legend">
              {SOURCES.map((s, i) => (
                <div key={i} className="donut-legend-row">
                  <span className="lg-dot" style={{ background: s.color }} />
                  <span style={{ color: 'var(--navy-800)' }}>{s.label}</span>
                  <b>{s.value} DT</b>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="tbl-card">
        <div className="tbl-toolbar">
          <h3 style={{ fontSize: '1rem', margin: 0 }}>Historique des livraisons payées</h3>
          <div style={{ flex: 1 }} />
          <button className="btn btn-sm btn-ghost"><DashIcon name="download" size={14} /> CSV</button>
        </div>
        <div className="tbl-scroll">
        <table className="tbl">
          <thead>
            <tr>
              <th>Date</th>
              <th>Référence</th>
              <th>Client</th>
              <th>Adresse</th>
              <th>COD</th>
              <th>Gain</th>
              <th>Statut</th>
            </tr>
          </thead>
          <tbody>
            {loadingDeliveries ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', color: 'var(--muted)', padding: 24 }}>Chargement…</td>
              </tr>
            ) : deliveries.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', color: 'var(--muted)', padding: 24 }}>Aucune livraison.</td>
              </tr>
            ) : deliveries.map((d) => (
              <tr key={d._id}>
                <td className="row-strong">{fmtDate(d.updatedAt ?? d.createdAt)}</td>
                <td className="row-id">{d.trackingCode}</td>
                <td>{d.dropoff.contactName ?? '—'}</td>
                <td style={{ color: 'var(--muted)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {d.dropoff.address}
                </td>
                <td className="mono">{d.price?.base ?? 0} DT</td>
                <td className="mono" style={{ color: 'var(--green-700)', fontWeight: 600 }}>
                  +{d.price?.livreurAmount ?? 0} DT
                </td>
                <td><StatusBadge status={d.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </>
  )
}
